import { useEffect, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { Icon } from '~/global/components/icon/Icon';
import { cn } from '@/lib/utils';
import { FullHeightWrapper } from '~/layout/components/full-height-wrapper/FullHeightWrapper';
import { logger } from '@/lib/logger';
import { usePurchases } from '~/purchases/contexts/purchases/Purchases';
import { useAiCredits } from '~/purchases/hooks/useAiCredits';
import type { SubscriptionScreenProps } from './SubscriptionScreen';

/**
 * Credit top-up view shown (in place of the plan tabs) when the user already has
 * the AI Coach subscription — they don't need to subscribe again, they need to
 * refill their FYT wallet. Renders the current balance, the consumable credit
 * packs from RevenueCat's `credits` offering (live prices + FYT amounts), and the
 * "Cancel subscription" / "Restore" footer.
 *
 * Presented both as the modal body (via `onRequestClose`, see `PaywallModal`) and
 * as the full subscription route — mirroring {@link SubscriptionScreen}.
 */
export const CreditTopUp = ({ onRequestClose }: SubscriptionScreenProps) => {
  const { t } = useTranslation('subscription');
  const router = useRouter();
  const {
    isReady,
    creditPackages,
    purchasePackage,
    refresh,
    canManageSubscriptions,
    openManageSubscriptions,
    restorePurchases,
  } = usePurchases();
  const { credits, refetch } = useAiCredits();

  const [purchasingId, setPurchasingId] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);

  // Pull a fresh balance whenever the top-up view opens. The shared `AiCredits`
  // Apollo entry is cache-first, so after a Free→AI-Coach upgrade it would
  // otherwise keep showing the stale drained `0` (core-engine reads the real
  // balance live from RevenueCat). This also un-sticks the chat send gate, which
  // reads the same cached entry.
  useEffect(() => {
    void refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBuy = async (identifier: string) => {
    const pkg = creditPackages.find((p) => p.identifier === identifier);
    if (!pkg) {
      return;
    }
    setPurchasingId(identifier);
    try {
      const result = await purchasePackage(pkg);
      if (result.success) {
        // The FYT grant is applied server-side by RevenueCat; re-pull the balance
        // (it may lag briefly behind core-engine's short RC cache) and customer info.
        await Promise.all([refetch(), refresh()]);
      }
    } catch (e) {
      logger.error(e, '[CreditTopUp] purchase failed');
    } finally {
      setPurchasingId(null);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    try {
      await restorePurchases();
    } catch (e) {
      logger.error(e, '[CreditTopUp] restore failed');
    } finally {
      setRestoring(false);
    }
  };

  const embedded = !!onRequestClose;

  const closeButton = (onRequestClose || router.canGoBack()) && (
    <Pressable
      onPress={() => (onRequestClose ? onRequestClose() : router.back())}
      hitSlop={8}
      className="p-1 active:opacity-70"
      accessibilityRole="button"
    >
      <Icon name="X" size={24} />
    </Pressable>
  );

  const header = (
    <View className="flex-row items-start justify-between gap-x-2">
      <View className="flex-1 gap-y-1">
        <Text className="text-2xl font-bold">{t('topUp.title')}</Text>
        <Text className="text-sm text-text-secondary">
          {t('topUp.subtitle')}
        </Text>
      </View>
      {closeButton}
    </View>
  );

  const scroll = (
    <ScrollView
      className="flex-1"
      contentContainerClassName={cn(
        'items-center',
        embedded ? 'px-6 pb-6 pt-4 md:px-4' : 'px-6 py-4 md:px-4 md:pt-20',
      )}
      showsVerticalScrollIndicator={false}
    >
      <View className="gap-y-6 w-full max-w-lg pb-10">
        {!embedded && header}

        {/* Current balance */}
        <View className="rounded-2xl bg-surface-elevated px-5 py-4">
          <Text className="text-sm text-text-secondary">
            {t('topUp.balanceLabel')}
          </Text>
          <Text className="text-3xl font-extrabold">
            {credits
              ? t('topUp.balance', { amount: credits.balance })
              : t('topUp.balanceUnknown')}
          </Text>
        </View>

        {/* Credit packs */}
        <View className="gap-y-3">
          {creditPackages.map((pkg) => (
            <View
              key={pkg.identifier}
              className="flex-row items-center justify-between rounded-2xl border border-border px-5 py-4"
            >
              <View className="flex-1 gap-y-0.5">
                <Text className="text-base font-semibold">
                  {pkg.credits != null
                    ? t('topUp.packCredits', { amount: pkg.credits })
                    : t('topUp.pack')}
                </Text>
                <Text className="text-sm text-text-secondary">
                  {pkg.priceString}
                </Text>
              </View>
              <Button
                size="sm"
                onPress={() => handleBuy(pkg.identifier)}
                disabled={!isReady || !!purchasingId}
                loading={purchasingId === pkg.identifier}
              >
                <Text>{t('topUp.buy')}</Text>
              </Button>
            </View>
          ))}
        </View>

        {isReady && creditPackages.length === 0 && (
          <Text className="text-center text-xs text-text-secondary">
            {t('topUp.unavailable')}
          </Text>
        )}
        {!isReady && (
          <Text className="text-center text-xs text-text-secondary">
            {t('loading')}
          </Text>
        )}

        {/* Manage / cancel + restore */}
        <View className="gap-y-1">
          {canManageSubscriptions && (
            <Button
              variant="link"
              size="sm"
              onPress={() => void openManageSubscriptions()}
              className="self-center"
            >
              <Text className="text-sm text-text-secondary">{t('cancel')}</Text>
            </Button>
          )}
          <Button
            variant="link"
            size="sm"
            onPress={handleRestore}
            loading={restoring}
            className="self-center"
          >
            <Text className="text-sm text-text-secondary">{t('restore')}</Text>
          </Button>
        </View>
      </View>
    </ScrollView>
  );

  if (embedded) {
    return (
      <View className="flex-1 bg-background">
        <View className="items-center border-b border-border px-6 pb-4 pt-5 md:px-4">
          <View className="w-full max-w-lg">{header}</View>
        </View>
        {scroll}
      </View>
    );
  }

  return <FullHeightWrapper>{scroll}</FullHeightWrapper>;
};
